import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { Label, TagGeneral } from '@spark';
import '@/shared/styles/TagInput.css';

function splitRawItems(raw, splitter = /[,\n\r]+/) {
    return String(raw || '')
        .split(splitter)
        .map((p) => p.trim())
        .filter(Boolean);
}

function appendUnique(current, parts) {
    const next = [...current];
    parts.forEach((p) => {
        if (!next.some((item) => item.toLowerCase() === p.toLowerCase())) next.push(p);
    });
    return next;
}

/**
 * Free-form tag input: Enter/comma to add, comma-separated paste, inline
 * remove, and Cmd/Ctrl+A (empty draft) to select every tag so Backspace/Delete
 * clears them. Shared by task labels and invite-user emails.
 *
 * `readOnly` renders the current tags as a plain chip list: no text input, no
 * per-tag remove buttons, no hint, no suffix, and no file drop — used where
 * values are display-only (e.g. the Duplicate Task modal's copied labels).
 */
const TagInput = forwardRef(({
    value,
    values,
    onChange,
    placeholder = 'Type/paste values separated with commas',
    ariaLabel,
    suffix = null,
    error = null,
    onFileDrop,
    wrapClassName = '',
    id,
    label,
    showLabel = false,
    showAsterisk = false,
    showHint = true,
    readOnly = false,
    splitPattern = /[,\n\r]+/,
    commitKeys = ['Enter', ','],
}, ref) => {
    const tags = Array.isArray(values) ? values : (Array.isArray(value) ? value : []);
    const [draft, setDraft] = useState('');
    const [allSelected, setAllSelected] = useState(false);
    const [isDragOver, setIsDragOver] = useState(false);
    const inputRef = useRef(null);

    const commitParts = (raw, current = tags) => {
        const parts = splitRawItems(raw, splitPattern);
        if (!parts.length) return current;
        const next = appendUnique(current, parts);
        const changed = next.length !== current.length
            || next.some((item, i) => item !== current[i]);
        if (changed) onChange?.(next);
        return next;
    };

    const addItems = (raw) => {
        commitParts(raw);
    };

    const commitDraft = (current = tags) => {
        if (!draft.trim()) return current;
        const next = commitParts(draft, current);
        setDraft('');
        return next;
    };

    const removeItem = (item) => {
        setAllSelected(false);
        onChange?.(tags.filter((t) => t !== item));
    };

    useImperativeHandle(ref, () => ({
        flush: () => commitDraft(),
        focus: () => inputRef.current?.focus(),
    }), [draft, tags, onChange, splitPattern]);

    useEffect(() => {
        inputRef.current?.scrollIntoView?.({ block: 'nearest' });
    }, [tags.length]);

    const handleKeyDown = (e) => {
        if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'a' && !draft && tags.length) {
            e.preventDefault();
            setAllSelected(true);
            return;
        }
        if (allSelected && (e.key === 'Backspace' || e.key === 'Delete')) {
            e.preventDefault();
            setAllSelected(false);
            onChange?.([]);
            return;
        }
        if (allSelected && !['Meta', 'Control', 'Shift', 'Alt'].includes(e.key)) {
            setAllSelected(false);
        }
        if (e.key === 'Enter' || commitKeys.includes(e.key)) {
            e.preventDefault();
            commitDraft();
        } else if (e.key === 'Backspace' && !draft && tags.length) {
            removeItem(tags[tags.length - 1]);
        }
    };

    const handlePaste = (e) => {
        const text = e.clipboardData?.getData('text') || '';
        if (splitPattern.test(text) && splitRawItems(text, splitPattern).length) {
            e.preventDefault();
            addItems(text);
            setDraft('');
        }
    };

    const handleDragOver = (e) => {
        if (!onFileDrop) return;
        e.preventDefault();
        setIsDragOver(true);
    };

    const handleDragLeave = (e) => {
        if (!onFileDrop) return;
        if (!e.currentTarget.contains(e.relatedTarget)) setIsDragOver(false);
    };

    const handleDrop = async (e) => {
        if (!onFileDrop) return;
        e.preventDefault();
        setIsDragOver(false);
        await onFileDrop(e.dataTransfer?.files?.[0]);
    };

    const wrapClasses = ['tag-input-wrap', wrapClassName].filter(Boolean).join(' ');
    const fieldClasses = [
        'tag-input',
        readOnly ? 'tag-input--readonly' : '',
        isDragOver ? 'tag-input--dragover' : '',
        allSelected ? 'tag-input--all-selected' : '',
        error ? 'tag-input--danger' : '',
    ].filter(Boolean).join(' ');

    return (
        <div className={wrapClasses}>
            {showLabel ? (
                <Label
                    label={label}
                    htmlFor={id}
                    showAsterisk={showAsterisk}
                />
            ) : null}
            <div className="tag-input__field-wrap">
                <div
                    className={fieldClasses}
                    onClick={readOnly ? undefined : () => inputRef.current?.focus()}
                    onDragOver={readOnly ? undefined : handleDragOver}
                    onDragLeave={readOnly ? undefined : handleDragLeave}
                    onDrop={readOnly ? undefined : handleDrop}
                    role={readOnly ? 'list' : 'textbox'}
                    tabIndex={-1}
                    aria-label={ariaLabel || (typeof label === 'string' ? label : undefined)}
                    aria-readonly={readOnly || undefined}
                >
                    <div className="tag-input__tags">
                        {tags.map((tag) => (
                            <TagGeneral
                                key={tag}
                                label={tag}
                                variant={readOnly ? 'default' : 'closeable'}
                                showClose={!readOnly}
                                onClose={readOnly ? undefined : () => removeItem(tag)}
                            />
                        ))}
                        {!readOnly && (
                            <input
                                ref={inputRef}
                                id={id}
                                className="tag-input__field"
                                value={draft}
                                placeholder={tags.length ? '' : placeholder}
                                onChange={(e) => setDraft(e.target.value)}
                                onKeyDown={handleKeyDown}
                                onPaste={handlePaste}
                                onBlur={() => {
                                    setAllSelected(false);
                                    commitDraft();
                                }}
                            />
                        )}
                    </div>
                    {!readOnly && suffix ? <div className="tag-input__suffix">{suffix}</div> : null}
                </div>
            </div>
            {error ? (
                <div className="tag-input__error" role="alert">
                    {error}
                </div>
            ) : null}
            {showHint && !readOnly ? (
                <div className="tag-input__hint">
                    Press <span className="tag-input__hint-key">↵ Enter</span> to add
                </div>
            ) : null}
        </div>
    );
});

TagInput.displayName = 'TagInput';

export default TagInput;
export { splitRawItems, appendUnique };
